import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { ClashFilterByHourAndLocal } from '@models/clash/clash-form.dto';
import { ClashListDto } from '@models/clash/clashes/clash-list-dto';
import { RecordClashesForm } from '@models/clash/clashes/record-clashes-form';
import { ClashBaseForm } from '@models/clash/clashes/registration-fight-dto';
import {
  BackendEmbateDto,
  ClashByHourDto,
  GamesTableDto,
  GamesTableRoundDto,
} from '@models/embate/games-table-dto';
import { IResponseDto, ResponseDto } from '@models/exceptions/response-dto';
import { IPage } from '@models/pagination/page';
import { PageRequest } from '@models/pagination/page-request';
import { extractResponse } from '@shared/rxjs/custom-rxjs-operators';
import { BaseService } from '@shared/services/base-service';
import { catchError, EMPTY, finalize, map, Observable, tap } from 'rxjs';
import { toParams } from 'src/app/core/utilities/utility-functions';

@Injectable({
  providedIn: 'root',
})
export class ClashRegistrationService extends BaseService {
  url = environment.apiUrl + '/v1/embates';
  urlv2 = environment.apiUrl + '/v2/embates';

  saveRegistraionClash(
    form: ClashBaseForm,
  ): Observable<ResponseDto<ClashListDto>> {
    return this.http.post<ResponseDto<ClashListDto>>(`${this.url}`, form).pipe(
      catchError((err) => {
        this.loadingService.deactivate();
        this.messageErrorResolver.resolveMessage(err);
        return EMPTY;
      }),
      tap(() => this.loadingService.deactivate()),
    );
  }

  updateRegistraionClash(
    form: ClashBaseForm,
  ): Observable<ResponseDto<RecordClashesForm>> {
    return this.http
      .put<ResponseDto<RecordClashesForm>>(`${this.url}`, form)
      .pipe(
        catchError((err) => {
          this.loadingService.deactivate();
          this.messageErrorResolver.resolveMessage(err);
          return EMPTY;
        }),
        tap(() => this.loadingService.deactivate()),
      );
  }

  clashConcluded(
    embNrId: number,
    embBlResultadoDefinido: boolean,
  ): Observable<ResponseDto<void>> {
    let params = new HttpParams();
    params = params.set('embBlResultadoDefinido', embBlResultadoDefinido);
    return this.http
      .patch<
        ResponseDto<void>
      >(`${this.url}/${embNrId}/concluido`, null, { params })
      .pipe(
        catchError((err) => {
          this.loadingService.deactivate();
          this.messageErrorResolver.resolveMessage(err);
          return EMPTY;
        }),
        tap(() => this.loadingService.deactivate()),
      );
  }

  getRegistraionClash(
    filtro?: IFiltroEmbateForm,
    pageable?: PageRequest,
  ): Observable<IPage<ClashListDto>> {
    let params = toParams({ filtro: filtro, pageable: pageable });
    return this.http
      .get<ResponseDto<IPage<ClashListDto>>>(this.url, { params })
      .pipe(
        map((response) => response.response),
        catchError((err) => {
          this.loadingService.deactivate();
          this.messageErrorResolver.resolveMessage(err);
          throw err;
        }),
      );
  }

  getRegistraionClashByEmbNrId(
    embNrId?: number,
  ): Observable<IPage<ClashListDto>> {
    return this.http
      .get<ResponseDto<IPage<ClashListDto>>>(this.url + `?embNrId=${embNrId}`)
      .pipe(
        map((response) => response.response),
        catchError((err) => {
          this.loadingService.deactivate();
          this.messageErrorResolver.resolveMessage(err);
          throw err;
        }),
      );
  }

  drawClashes(cheNrId: number): Observable<ResponseDto<ClashListDto>> {
    return this.http
      .post<ResponseDto<ClashListDto>>(this.url + `/${cheNrId}/sortear`, {})

      .pipe(
        catchError((err) => {
          this.loadingService.deactivate();
          this.messageErrorResolver.resolveMessage(err);
          return EMPTY;
        }),
        tap(() => this.loadingService.deactivate()),
      );
  }

  removeParticipantFromClash(
    embNrId: number,
    ichNrId: number,
  ): Observable<ResponseDto<void>> {
    console.log('Removing participant from clash', embNrId, ichNrId);
    return this.http
      .delete<
        ResponseDto<void>
      >(this.url + `/${embNrId}/remover-inscricao/${ichNrId}`)
      .pipe(
        catchError((err) => {
          this.loadingService.deactivate();
          this.messageErrorResolver.resolveMessage(err);
          return EMPTY;
        }),
        tap(() => this.loadingService.deactivate()),
      );
  }

  drawClashesGeneral(cheNrId: number): Observable<ResponseDto<ClashListDto>> {
    return this.http.post<ResponseDto<ClashListDto>>(
      this.url + `/${cheNrId}/sortear-geral`,
      {},
    );
  }
  deleteClash(embNrId: number): Observable<ResponseDto<void>> {
    return this.http.delete<ResponseDto<void>>(
      this.url + `/deletar/${embNrId}`,
    );
  }

  getClashsByHourAndLocal(
    clashFilter: ClashFilterByHourAndLocal,
  ): Observable<GamesTableDto[]> {
    let params = toParams({ clashFilter: clashFilter });

    return this.http
      .get<IResponseDto<GamesTableRoundDto[]>>(`${this.urlv2}`, { params: params })
      .pipe(
        extractResponse(),
        map((rounds) => this.flattenGamesTable(rounds ?? [])),
      );
  }

  private flattenGamesTable(rounds: GamesTableRoundDto[]): GamesTableDto[] {
    return rounds.flatMap((round) =>
      (round.espacos ?? []).map((space) => ({
        embDtEmbate: this.parseLocalDate(round.embDtEmbate),
        areNrId: space.areNrId,
        areTxNome: space.areTxNome,
        epcNrId: space.epcNrId,
        epcTxNome: space.epcTxNome,
        logTxLogradouro: space.logTxLogradouro ?? '',
        embListEmbates: (space.eprListEspacoVaga ?? [])
          .filter((slot) => slot.embateDto)
          .map((slot) => this.toClashByHour(slot.embateDto!, slot, round)),
      })),
    );
  }

  private parseLocalDate(date: string): Date {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private toClashByHour(
    embate: BackendEmbateDto,
    slot: GamesTableRoundDto['espacos'][number]['eprListEspacoVaga'][number],
    round: GamesTableRoundDto,
  ): ClashByHourDto {
    const categoria = embate.parametrizacaoEtapaDto?.sorObjCategoria;
    const inscricao = embate.embListInscricaoDto?.[0];
    const petNrId = embate.parametrizacaoEtapaDto?.petNrId
      ?? inscricao?.embateDto?.inscricaoEtapaDto?.petNrId
      ?? 0;

    return {
      embNrId: embate.embNrId,
      embTmEmbate: slot.epvTmInicio ?? '',
      embBlResultadoDefinido: embate.embBlResultadoDefinido,
      embNrEtapa: embate.embNrEtapa,
      rodadaDto: embate.rodadaDto ?? round.rodada,
      embNrOrdemJogo: slot.epvNrOrdemVaga ?? embate.embNrOrdemJogo,
      chaveEmbateSimplesDto: {
        ...(embate.chaveEmbateSimplesDto ?? {
          cheNrId: embate.cheNrId ?? 0,
          cheTxNome: embate.cheTxNome ?? '',
          petNrId,
          petTxNome: embate.parametrizacaoEtapaDto?.petTxNome ?? '',
          catNrId: categoria?.catNrId ?? 0,
          catTxSexo: categoria?.catTxSexo as any,
          catTxNome: categoria?.catTxNome ?? '',
          comNrId: categoria?.catObjCompeticao?.comNrId ?? 0,
          comTxNome: categoria?.catObjCompeticao?.comTxNome ?? '',
        }),
      },
      embListInscricoes: embate.embListInscricaoDto ?? [],
    };
  }

  generateClashes(petNrId: number): Observable<void> {
    return this.http
      .post<
        ResponseDto<void>
      >(this.url + `/gerar-confrontos/${petNrId}/MODELO_CBDE`, {})
      .pipe(extractResponse());
  }

  definirLocalHoraEmbate(
    form: DefinirEmbateForm,
  ): Observable<void> {
    return this.http
      .put<void>(
        this.url + `/definir-vaga-embate`,
        form,
      )
      .pipe(finalize(() => this.loadingService.deactivate()));
  }
}
export interface IFiltroEmbateForm {
  embNrId?: number;
  cheNrId?: number;
  rodNrId: number;
  petNrIds?: number[];
  catNrId?: number;
  comNrId?: number;
  epcNrId?: number;
  areNrId?: number;
  hadNrId?: number;
  plaNrId?: number;
  embDtEmbate?: string;
  embTmEmbate?: string;
  embBlEmbatesSemLocal?: boolean;
}

export interface DefinirEmbateForm {
  petNrId: number;
  epvNrId: number;
  embNrId: number;
}
